import type { ManufacturerProfile, PartCategory, SourcePriorityRule, TaxonomyProductMapping } from "../domain/types";

export type DemoGeneratorConfig = {
    seed: number;
    partCount: number;
    priceUpdateCount: number;
    pricingHistoryCount: number;
    opportunityCount: number;
    purchaseOrderCount: number;
    catalogCount: number;
};

export const DEFAULT_DEMO_GENERATOR_CONFIG: DemoGeneratorConfig = {
    seed: 20260501,
    partCount: 10000,
    priceUpdateCount: 25000,
    pricingHistoryCount: 2500,
    opportunityCount: 500,
    purchaseOrderCount: 300,
    catalogCount: 2500,
};

export const MANUFACTURERS: ManufacturerProfile[] = [
    {
        id: "MFR-001",
        name: "Texas Instruments",
        preferredCategory: "Semiconductor",
        autoApproveThresholdPct: 5,
        reviewThresholdPct: 10,
        breachAction: "HOLD + escalate to procurement",
        volatilityIndex: 0.14,
        contractPricing: true,
    },
    {
        id: "MFR-002",
        name: "Murata",
        preferredCategory: "Capacitor",
        autoApproveThresholdPct: 35,
        reviewThresholdPct: 50,
        breachAction: "FLAG only",
        volatilityIndex: 0.68,
        contractPricing: false,
    },
    {
        id: "MFR-003",
        name: "Yageo",
        preferredCategory: "Resistor",
        autoApproveThresholdPct: 20,
        reviewThresholdPct: 40,
        breachAction: "FLAG + notify buyer",
        volatilityIndex: 0.44,
        contractPricing: false,
    },
    {
        id: "MFR-004",
        name: "Vishay",
        preferredCategory: "Resistor",
        autoApproveThresholdPct: 15,
        reviewThresholdPct: 25,
        breachAction: "FLAG for review",
        volatilityIndex: 0.28,
        contractPricing: false,
    },
    {
        id: "MFR-005",
        name: "STMicroelectronics",
        preferredCategory: "Semiconductor",
        autoApproveThresholdPct: 12,
        reviewThresholdPct: 22,
        breachAction: "FLAG + notify product manager",
        volatilityIndex: 0.35,
        contractPricing: false,
    },
    {
        id: "MFR-999",
        name: "Unknown / New",
        preferredCategory: "Connector",
        autoApproveThresholdPct: 0,
        reviewThresholdPct: 0,
        breachAction: "ALWAYS hold",
        volatilityIndex: 0.9,
        contractPricing: false,
    },
];

export const PART_CATEGORIES: PartCategory[] = ["Semiconductor", "Capacitor", "Resistor", "Connector"];

export const TAXONOMY_MAPPINGS: TaxonomyProductMapping[] = [
    {
        taxonomyPath: "Semiconductor > Microcontroller > 32-bit",
        erpProductGroup: "SEM-MCU",
        erpProductLine: "Embedded Control",
        crmFamily: "Microcontrollers",
    },
    {
        taxonomyPath: "Semiconductor > Power Management > Regulator",
        erpProductGroup: "SEM-PWR",
        erpProductLine: "Power Management",
        crmFamily: "Power ICs",
    },
    {
        taxonomyPath: "Passive > Capacitor > Ceramic MLCC",
        erpProductGroup: "PAS-CAP",
        erpProductLine: "Ceramic Capacitors",
        crmFamily: "Capacitors",
    },
    {
        taxonomyPath: "Passive > Resistor > Thick Film",
        erpProductGroup: "PAS-RES",
        erpProductLine: "Chip Resistors",
        crmFamily: "Resistors",
    },
    {
        taxonomyPath: "Interconnect > Connector > Board-to-board",
        erpProductGroup: "INT-CON",
        erpProductLine: "Connectors",
        crmFamily: "Interconnect",
    },
];

export const SOURCE_PRIORITY_RULES: SourcePriorityRule[] = [
    {
        attributeName: "lifecycleStatus",
        manufacturerDirectRank: 1,
        enrichmentProviderRank: 2,
        supplierSubmittedRank: 3,
        defaultAction: "Keep manufacturer-direct value and log override",
    },
    {
        attributeName: "rohsStatus",
        manufacturerDirectRank: 1,
        enrichmentProviderRank: 2,
        supplierSubmittedRank: 3,
        defaultAction: "Route conflict to compliance review",
    },
    {
        attributeName: "packageType",
        manufacturerDirectRank: 1,
        enrichmentProviderRank: 2,
        supplierSubmittedRank: 3,
        defaultAction: "Use highest ranked source",
    },
    {
        attributeName: "pricing",
        manufacturerDirectRank: 2,
        enrichmentProviderRank: 3,
        supplierSubmittedRank: 1,
        defaultAction: "Use supplier price but compare against history",
    },
];

export const taxonomyForCategory = (category: PartCategory, index: number): TaxonomyProductMapping => {
    const candidates = TAXONOMY_MAPPINGS.filter((mapping) => {
        if (category === "Semiconductor") return mapping.taxonomyPath.startsWith("Semiconductor");
        if (category === "Capacitor") return mapping.taxonomyPath.includes("Capacitor");
        if (category === "Resistor") return mapping.taxonomyPath.includes("Resistor");
        return mapping.taxonomyPath.includes("Connector");
    });

    return candidates[index % candidates.length]!;
};

